import React from 'react';
import EntityLogo from '@/assets/logos/EntityLogo';
import { buildInvoicePaymentReference } from '@/utils/invoiceReference';
import { normalizePhoneForStorage } from '@/utils/phone';
import { formatStripePaymentLabelForPdf, getVisibleStripePaymentUrl } from '@/utils/stripePaymentLabel';
import { STRIPE_SURCHARGE_EXPIRY_DISPLAY_DATE } from '@/utils/stripeSurchargePolicy';

type Category = {
  CategoryId: number;
  CategoryName: string;
  IsActive?: boolean;
};

type ItemRow = {
  categoryId: string;
  description: string;
  quantity: string;
  unitPrice: string;
};

type EditorForm = {
  invoiceType: 'standard' | 'inspection';
  invoiceNumber: string;
  rfiTicketNumber?: string;
  projectNumber: string;
  entity: string;
  clientName: string;
  clientCompany: string;
  clientAddress: string;
  clientMobile: string;
  clientEmail: string;
  projectAddress: string;
  invoiceDate: string;
  dueDate: string;
  stripePaymentMode: 'none' | 'payment_link' | 'checkout_session';
  stripeSurchargeEnabled: boolean;
  stripePaymentUrl?: string;
};
type EditableInvoiceField = Exclude<keyof EditorForm, 'stripeSurchargeEnabled'>;

type ClientChoice = {
  ClientId?: number;
  Name: string;
  Company?: string;
  Email?: string;
  Phone?: string;
  Address?: string;
};

type SenderProfile = {
  displayName: string;
  address: string;
  mobile: string;
  email: string;
  payId?: string;
  bsb: string;
  accountNumber: string;
  accountName: string;
};

type Props = {
  editorForm: EditorForm;
  items: ItemRow[];
  categories: Category[];
  selectableClients: ClientChoice[];
  selectedClientId: string;
  clientSearchQuery: string;
  filteredClients: ClientChoice[];
  clientSearchStatus: string;
  hasClientSearchQuery: boolean;
  sender: SenderProfile;
  totals: { subTotal: number; gst: number; grandTotal: number };
  mode: 'create' | 'amend';
  busy: string | null;
  feedbackTone?: 'danger' | 'success' | 'warning' | null;
  feedbackMessage?: string | null;
  showStripePaymentLinkOption: boolean;
  stripeSurchargeAllowed: boolean;
  onUpdateForm: (field: EditableInvoiceField, value: string) => void;
  onToggleStripeSurcharge: (enabled: boolean) => void;
  onUpdateItem: (index: number, field: keyof ItemRow, value: string) => void;
  onProjectQueryChange: (value: string) => void;
  onProjectBlur: () => void;
  onSelectClient: (clientId: string) => void;
  onClientSearchQueryChange: (value: string) => void;
  onApplyClient: (client: ClientChoice) => void;
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  onPreview: () => void;
  onFinalise: () => void;
};

const inputBase: React.CSSProperties = {
  width: '100%',
  border: '1px solid rgba(144, 163, 195, 0.34)',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 14,
  boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.98)',
  color: '#10243f',
};

const sectionCard: React.CSSProperties = {
  border: '1px solid rgba(144, 163, 195, 0.22)',
  borderRadius: 18,
  background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,249,253,0.98) 100%)',
  padding: 16,
  display: 'grid',
  gap: 12,
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)',
};

const compactProjectCard: React.CSSProperties = {
  ...sectionCard,
  borderRadius: 14,
  padding: 12,
  gap: 8,
};

const compactProjectTopRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 8,
};

const sectionEyebrow: React.CSSProperties = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#34579e',
  fontWeight: 800,
};

const dateGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 10,
};

const dateFieldLabel: React.CSSProperties = {
  ...sectionEyebrow,
  fontSize: 11,
  letterSpacing: '0.05em',
};

const dateInput: React.CSSProperties = {
  ...inputBase,
  padding: '10px 8px',
  fontSize: 13,
};

const stripePaymentPreviewLinkStyle: React.CSSProperties = {
  color: '#34579e',
  fontWeight: 700,
  textDecoration: 'underline',
};

const compactProjectTextarea: React.CSSProperties = {
  ...inputBase,
  minHeight: 64,
  resize: 'vertical',
};

const compactProjectInlineInput: React.CSSProperties = {
  ...inputBase,
  padding: '10px 8px',
  fontSize: 13,
};

const compactTotalDueRow: React.CSSProperties = {
  borderRadius: 10,
  background: 'linear-gradient(180deg, rgba(234,241,255,0.74) 0%, rgba(255,255,255,0.96) 100%)',
  padding: '8px 10px',
  border: '1px solid rgba(144, 163, 195, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const compactTotalDueValue: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  color: '#10243f',
  whiteSpace: 'nowrap',
};

const compactTotalDueLabel: React.CSSProperties = {
  ...sectionEyebrow,
  color: '#5d6d84',
  fontSize: 10,
  letterSpacing: '0.05em',
  lineHeight: 1.05,
};

const fieldLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: '#5d6d84',
};

const moneyInputWrap: React.CSSProperties = {
  position: 'relative',
  display: 'grid',
};

const moneyInputPrefix: React.CSSProperties = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#64748b',
  fontSize: 13,
  fontWeight: 800,
  pointerEvents: 'none',
};

const moneyInput: React.CSSProperties = {
  ...inputBase,
  paddingLeft: 24,
};

const darkClientCard: React.CSSProperties = {
  ...sectionCard,
  background: 'linear-gradient(180deg, #35527f 0%, #314865 100%)',
  color: '#fff',
  border: '1px solid rgba(68, 92, 128, 0.7)',
};

const addItemButton: React.CSSProperties = {
  border: '1px dashed rgba(47, 111, 212, 0.55)',
  borderRadius: 8,
  background: 'rgba(234, 241, 255, 0.96)',
  color: '#234ea8',
  fontWeight: 800,
  fontSize: 12,
  minHeight: 30,
  padding: '6px 10px',
  cursor: 'pointer',
};

const removeItemButton: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  background: 'rgba(194, 56, 56, 0.12)',
  color: '#a02f2f',
  fontSize: 11,
  fontWeight: 800,
  padding: '6px 10px',
  cursor: 'pointer',
};

const secondaryButton: React.CSSProperties = {
  border: '1px solid rgba(144, 163, 195, 0.28)',
  borderRadius: 10,
  background: '#fff',
  color: '#31435d',
  fontWeight: 700,
  padding: '12px 14px',
  cursor: 'pointer',
};

const primaryButton: React.CSSProperties = {
  border: 'none',
  borderRadius: 10,
  background: 'linear-gradient(135deg, #234ea8 0%, #2f6fd4 100%)',
  color: '#fff',
  fontWeight: 800,
  padding: '12px 16px',
  cursor: 'pointer',
  boxShadow: '0 12px 28px rgba(35, 78, 168, 0.22)',
};

const actionGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 10,
};

const feedbackToneStyles: Record<'danger' | 'success' | 'warning', React.CSSProperties> = {
  danger: { color: '#972a2a', background: 'rgba(252, 236, 236, 0.92)', borderColor: 'rgba(193, 56, 56, 0.18)' },
  success: { color: '#0b6a4c', background: 'rgba(233, 247, 241, 0.92)', borderColor: 'rgba(15, 138, 99, 0.18)' },
  warning: { color: '#8c5013', background: 'rgba(255, 246, 233, 0.92)', borderColor: 'rgba(192, 108, 22, 0.18)' },
};

function formatMoney(amount: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(Number.isFinite(amount) ? amount : 0);
}

const InvoiceWorkflowPhoneSheet: React.FC<Props> = ({
  editorForm,
  items,
  categories,
  selectableClients,
  selectedClientId,
  clientSearchQuery,
  filteredClients,
  clientSearchStatus,
  hasClientSearchQuery,
  sender,
  totals,
  mode,
  busy,
  feedbackTone,
  feedbackMessage,
  showStripePaymentLinkOption,
  stripeSurchargeAllowed,
  onUpdateForm,
  onToggleStripeSurcharge,
  onUpdateItem,
  onProjectQueryChange,
  onProjectBlur,
  onSelectClient,
  onClientSearchQueryChange,
  onApplyClient,
  onAddItem,
  onRemoveItem,
  onPreview,
  onFinalise,
}) => {
  const isInspectionInvoice = editorForm.invoiceType === 'inspection';
  const paymentReference = String(editorForm.invoiceNumber || '').trim()
    || buildInvoicePaymentReference('', editorForm.rfiTicketNumber)
    || 'Invoice number pending';
  const visibleStripePaymentUrl = getVisibleStripePaymentUrl(editorForm.entity, editorForm.stripePaymentUrl);
  const visibleClientChoices = hasClientSearchQuery ? filteredClients : selectableClients.slice(0, 4);

  return (
  <div data-invoice-phone-sheet="true" style={{ display: 'grid', gap: 14 }}>
    <div style={{ ...sectionCard, gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 8, borderBottom: '1px solid rgba(144, 163, 195, 0.2)' }}>
        <EntityLogo entity={editorForm.entity} size="horizontal" width={230} height={72} style={{ maxWidth: '100%', height: 'auto' }} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.08em', color: '#10243f' }}>INVOICE</div>
      <label style={{ display: 'grid', gap: 6 }}>
        <span style={sectionEyebrow}>Invoice no.</span>
        <input style={inputBase} value={editorForm.invoiceNumber} onChange={(event) => onUpdateForm('invoiceNumber', event.target.value)} />
      </label>
      <div style={dateGrid}>
        <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
          <span style={dateFieldLabel}>Date</span>
          <input style={dateInput} type="date" value={editorForm.invoiceDate} onChange={(event) => onUpdateForm('invoiceDate', event.target.value)} />
        </label>
        <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
          <span style={dateFieldLabel}>Due date</span>
          <input style={dateInput} type="date" value={editorForm.dueDate} onChange={(event) => onUpdateForm('dueDate', event.target.value)} />
        </label>
      </div>
    </div>

    <div style={compactProjectCard} aria-label={isInspectionInvoice ? 'Inspection and sender' : 'Project and sender'}>
      <div style={compactProjectTopRow}>
        <label style={{ display: 'grid', minWidth: 0 }}>
          <input
            style={compactProjectInlineInput}
            value={isInspectionInvoice ? (editorForm.invoiceNumber || 'INX pending') : editorForm.projectNumber}
            onChange={(event) => {
              if (isInspectionInvoice) return;
              onUpdateForm('projectNumber', event.target.value);
              onProjectQueryChange(event.target.value);
            }}
            onBlur={onProjectBlur}
            placeholder={isInspectionInvoice ? 'INX pending' : 'Project number'}
            aria-label={isInspectionInvoice ? 'INX number' : 'Project number'}
            readOnly={isInspectionInvoice}
          />
        </label>
        <label style={{ display: 'grid', minWidth: 0 }}>
          <select
            style={compactProjectInlineInput}
            value={editorForm.entity}
            onChange={(event) => onUpdateForm('entity', event.target.value)}
            aria-label="Sender entity"
          >
            <option value="Linfinity Engineering Pty Ltd">Linfinity Engineering Pty Ltd</option>
            <option value="Equilibrium Engineering Pty Ltd">Equilibrium Engineering Pty Ltd</option>
          </select>
        </label>
      </div>
      <label style={{ display: 'grid', gap: 6 }}>
        <textarea
          style={compactProjectTextarea}
          value={editorForm.projectAddress}
          onChange={(event) => onUpdateForm('projectAddress', event.target.value)}
          placeholder={isInspectionInvoice ? 'Inspection address' : 'Project address'}
          aria-label={isInspectionInvoice ? 'Inspection address' : 'Project address'}
        />
      </label>
      <div style={compactTotalDueRow}>
        <div style={compactTotalDueLabel}>
          <div>Total</div>
          <div>due</div>
        </div>
        <div style={compactTotalDueValue}>{formatMoney(totals.grandTotal)}</div>
      </div>
    </div>

    <div style={darkClientCard}>
      <div style={sectionEyebrow}>Invoice to</div>
      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.88)' }}>Search saved clients</span>
        <input
          style={{ ...inputBase, background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.24)' }}
          value={clientSearchQuery}
          onChange={(event) => onClientSearchQueryChange(event.target.value)}
          placeholder="Name, email, phone, address"
          aria-label="Search saved clients"
        />
      </label>
      <div style={{ display: 'grid', gap: 6 }}>
        {visibleClientChoices.map((client) => {
          const clientId = String(client.ClientId || '');
          const isSelected = clientId !== '' && clientId === selectedClientId;
          const secondaryText = [client.Company, client.Email || client.Phone, client.Address].filter(Boolean).join(' · ');
          return (
            <button
              key={String(client.ClientId || `${client.Name}-${client.Email || ''}-${client.Phone || ''}`)}
              type="button"
              onClick={() => {
                if (client.ClientId) onSelectClient(String(client.ClientId));
                onApplyClient(client);
              }}
              style={{
                borderRadius: 12,
                border: isSelected ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.16)',
                background: isSelected ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.16)',
                color: '#fff',
                textAlign: 'left',
                padding: '10px 12px',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800 }}>{client.Name}</div>
              {secondaryText ? (
                <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(255,255,255,0.78)' }}>{secondaryText}</div>
              ) : null}
            </button>
          );
        })}
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.74)' }}>
          {hasClientSearchQuery ? clientSearchStatus : 'Recent saved clients appear here. Type to narrow the list.'}
        </div>
      </div>
      <input style={{ ...inputBase, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }} value={editorForm.clientName} onChange={(event) => onUpdateForm('clientName', event.target.value)} placeholder="Client name" />
      <input style={{ ...inputBase, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }} value={editorForm.clientCompany} onChange={(event) => onUpdateForm('clientCompany', event.target.value)} placeholder="Company" />
      <textarea style={{ ...inputBase, minHeight: 72, resize: 'vertical', background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }} value={editorForm.clientAddress} onChange={(event) => onUpdateForm('clientAddress', event.target.value)} placeholder="Client address" />
      <input
        style={{ ...inputBase, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}
        type="tel"
        value={editorForm.clientMobile}
        onChange={(event) => onUpdateForm('clientMobile', event.target.value)}
        onBlur={() => onUpdateForm('clientMobile', normalizePhoneForStorage(editorForm.clientMobile) || '')}
        placeholder="Mobile"
      />
      <input style={{ ...inputBase, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }} value={editorForm.clientEmail} onChange={(event) => onUpdateForm('clientEmail', event.target.value)} placeholder="Email" />
    </div>

    <div style={sectionCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={sectionEyebrow}>Line items</div>
        <button type="button" onClick={onAddItem} style={addItemButton}>
          + Add item
        </button>
      </div>
      {items.map((row, index) => {
        const amount = Number(row.quantity || 0) * Number(row.unitPrice || 0);
        return (
          <div key={`mobile-row-${index}`} style={{ border: '1px solid rgba(144, 163, 195, 0.18)', borderRadius: 16, background: 'linear-gradient(180deg, rgba(242,246,252,0.96) 0%, rgba(255,255,255,0.98) 100%)', padding: 14, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontWeight: 800, color: '#10243f' }}>Item {index + 1}</div>
              <button type="button" onClick={() => onRemoveItem(index)} style={removeItemButton}>
                Remove
              </button>
            </div>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={fieldLabel}>Category</span>
              <select style={inputBase} value={row.categoryId} onChange={(event) => onUpdateItem(index, 'categoryId', event.target.value)}>
                <option value="">Category</option>
                {categories.filter((category) => category.IsActive !== false).map((category) => (
                  <option key={category.CategoryId} value={String(category.CategoryId)}>
                    {category.CategoryName}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={fieldLabel}>Description</span>
              <textarea style={{ ...inputBase, minHeight: 86, resize: 'vertical' }} value={row.description} onChange={(event) => onUpdateItem(index, 'description', event.target.value)} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={fieldLabel}>Rate</span>
                <span style={moneyInputWrap}>
                  <span style={moneyInputPrefix}>$</span>
                  <input style={moneyInput} type="number" step="0.01" value={row.unitPrice} onChange={(event) => onUpdateItem(index, 'unitPrice', event.target.value)} />
                </span>
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={fieldLabel}>Qty</span>
                <input style={inputBase} type="number" step="0.01" value={row.quantity} onChange={(event) => onUpdateItem(index, 'quantity', event.target.value)} />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: '#fff', borderRadius: 12, padding: '10px 12px', border: '1px solid rgba(144, 163, 195, 0.18)' }}>
              <span style={{ ...fieldLabel, color: '#5d6d84' }}>Line total</span>
              <strong style={{ color: '#10243f' }}>{formatMoney(amount)}</strong>
            </div>
          </div>
        );
      })}
    </div>

    <div style={{ ...sectionCard, gap: 10 }}>
      {showStripePaymentLinkOption ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={sectionEyebrow}>Online payment</div>
          <div style={{ display: 'grid', gap: 8 }}>
            <select style={inputBase} value={editorForm.stripePaymentMode} onChange={(event) => onUpdateForm('stripePaymentMode', event.target.value)}>
              <option value="none">Bank transfer only</option>
              <option value="payment_link">Stripe payment link</option>
              {editorForm.stripePaymentMode === 'checkout_session' ? (
                <option value="checkout_session">Legacy Stripe checkout link</option>
              ) : null}
            </select>
            {editorForm.stripePaymentMode === 'payment_link' && stripeSurchargeAllowed ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(144, 163, 195, 0.28)', background: '#f8fafc', color: '#1e293b', fontSize: 13, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={editorForm.stripeSurchargeEnabled}
                  onChange={(event) => onToggleStripeSurcharge(event.target.checked)}
                />
                <span>Add 1.7% surcharge</span>
              </label>
            ) : null}
          </div>
          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
            {editorForm.stripePaymentMode === 'payment_link'
              ? (!stripeSurchargeAllowed
                ? `Saving creates a Stripe Payment Link with no Stripe surcharge. Card surcharges are disabled after ${STRIPE_SURCHARGE_EXPIRY_DISPLAY_DATE}.`
                : editorForm.stripeSurchargeEnabled
                ? `Saving creates a Stripe Payment Link with a flat 1.7% Stripe Surcharge, expires after ${STRIPE_SURCHARGE_EXPIRY_DISPLAY_DATE}, and collects contact name, email, and business name on the hosted page.`
                : 'Saving creates a Stripe Payment Link with no Stripe surcharge added and collects contact name, email, and business name on the hosted page.')
              : editorForm.stripePaymentMode === 'checkout_session'
                ? 'This invoice currently has a legacy Stripe checkout link.'
                : 'Saving keeps this invoice on bank transfer / PayID only.'}
          </div>
          {visibleStripePaymentUrl ? (
            <a href={visibleStripePaymentUrl} target="_blank" rel="noreferrer" style={{ color: '#234ea8', fontWeight: 700, fontSize: 13 }}>
              Open current payment page
            </a>
          ) : null}
        </div>
      ) : null}
      <div style={actionGrid}>
        <button type="button" onClick={onPreview} disabled={busy === 'preview-draft' || busy === 'create' || busy === 'amend'} style={secondaryButton}>
          {busy === 'preview-draft' ? 'Rendering...' : 'Preview PDF'}
        </button>
        <button type="submit" disabled={busy === 'create' || busy === 'amend'} style={primaryButton}>
          {busy === 'create' || busy === 'amend'
            ? (mode === 'create' ? 'Saving...' : 'Updating...')
            : (mode === 'create' ? 'Save invoice' : 'Save changes')}
        </button>
      </div>
      <button type="button" onClick={onFinalise} disabled={busy === 'create' || busy === 'amend'} style={{ ...secondaryButton, width: '100%' }}>
        Finalise + send
      </button>
      {feedbackMessage && feedbackTone ? (
        <div
          role={feedbackTone === 'danger' ? 'alert' : 'status'}
          aria-live={feedbackTone === 'danger' ? 'assertive' : 'polite'}
          style={{
            border: '1px solid',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
            lineHeight: 1.45,
            fontWeight: 700,
            ...feedbackToneStyles[feedbackTone],
          }}
        >
          {feedbackMessage}
        </div>
      ) : null}
    </div>

    <div style={{ ...sectionCard, overflow: 'hidden', padding: 0, gap: 0 }}>
      <div style={{ padding: 16, borderBottom: '1px solid rgba(144, 163, 195, 0.18)' }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10, color: '#10243f' }}>Payment Methods</div>
        {sender.payId ? <div style={{ fontSize: 14, marginBottom: 6 }}><strong>PayID:</strong> {sender.payId}</div> : null}
        <div style={{ fontSize: 14, marginBottom: 6 }}><strong>Bank Transfer:</strong> BSB: {sender.bsb} &nbsp;&nbsp; Account No: {sender.accountNumber}</div>
        <div style={{ fontSize: 14, marginBottom: 6 }}><strong>Account Name:</strong> {sender.accountName}</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#a02f2f', marginTop: 10 }}>Please use reference: {paymentReference}</div>
        {visibleStripePaymentUrl ? (
          <div style={{ fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>
            <a href={visibleStripePaymentUrl} target="_blank" rel="noreferrer" style={stripePaymentPreviewLinkStyle}>
              {formatStripePaymentLabelForPdf(visibleStripePaymentUrl, editorForm.stripeSurchargeEnabled)}
            </a>
          </div>
        ) : null}
      </div>
      <div style={{ padding: 16, display: 'grid', gap: 8, fontSize: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>Sub Total:</span>
          <strong>{formatMoney(totals.subTotal)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>10% GST:</span>
          <strong>{formatMoney(totals.gst)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>Paid:</span>
          <strong>-$0.00</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, background: 'linear-gradient(135deg, #35527f 0%, #314865 100%)', color: '#fff', borderRadius: 10, padding: '10px 12px', fontSize: 20 }}>
          <span>Grand Total:</span>
          <strong>{formatMoney(totals.grandTotal)}</strong>
        </div>
      </div>
      <div style={{ borderTop: '1px solid rgba(144, 163, 195, 0.18)', padding: '16px 18px', fontSize: 13, color: '#31435d', lineHeight: 1.7 }}>
        <div style={{ fontWeight: 800 }}>{sender.displayName}</div>
        <div>Address: {sender.address}</div>
        <div>M: {sender.mobile}</div>
        <div>E: {sender.email}</div>
      </div>
    </div>
  </div>
  );
};

export default InvoiceWorkflowPhoneSheet;
